import type { ReactElement } from "react";
import { z } from "zod";
import type { WidgetKind } from "@playbookai/types";
import { DASHBOARD_WIDGET_CONTRACTS } from "@playbookai/widget-schemas";
import type { DashboardWidgetEditorProps } from "../types.js";

/**
 * ONE presentation-params editor for every LAMA report section that has params
 * (PB-191 Task 19).
 *
 * SCHEMA-DRIVEN rather than four hand-written forms, because LAMA's params are
 * all bounded integers. Home's editors are bespoke for a real reason — they pick
 * DOMAIN OBJECTS, which no generic form can render. Nothing here selects an
 * entity: the campaign scope comes from the dashboard subject and the connection
 * is template-owned.
 *
 * A FACTORY, not a component taking `kind`, because
 * `DashboardWidgetEditorProps` is `{ draftParams, onChange }` — widening a prop
 * type every widget consumes, to tell one family something its own def knows, is
 * the worse trade.
 *
 * THE SCHEMA IS THE ONLY SOURCE OF THE BOUNDS, so the input cannot drift from
 * what the server will accept — the same schema validates the PATCH.
 */

/** One editable field, as read off the contract's params schema. */
interface NumericField {
  readonly key: string;
  readonly min: number;
  readonly max: number;
}

/**
 * Read the bounded-integer fields out of a params schema.
 *
 * Returns [] for anything it does not understand: a form that guessed at an
 * unrecognised type would render a control that cannot produce a valid value, so
 * a schema this cannot express renders NOTHING and the empty state says so.
 */
function numericFields(schema: z.ZodTypeAny): NumericField[] {
  if (!(schema instanceof z.ZodObject)) return [];
  const shape = schema.shape as Record<string, z.ZodTypeAny>;
  const fields: NumericField[] = [];
  for (const [key, raw] of Object.entries(shape)) {
    const inner = raw instanceof z.ZodOptional ? raw.unwrap() : raw;
    if (!(inner instanceof z.ZodNumber)) continue;
    const min = inner.minValue;
    const max = inner.maxValue;
    // Both bounds required — an unbounded number input is how a chart gets
    // asked for 10,000 waves.
    if (min === null || max === null) continue;
    fields.push({ key, min, max });
  }
  return fields;
}

/** Human label per param key, kept beside the schema that names them. */
const LABELS: Readonly<Record<string, string>> = {
  limit: "Entries shown",
  waveWindow: "Waves shown",
};

const LEGEND =
  "block text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5";

export function lamaParamsEditor(kind: WidgetKind) {
  const schema = DASHBOARD_WIDGET_CONTRACTS.get(kind)?.paramsSchema;
  const fields = schema ? numericFields(schema) : [];

  function LamaParamsEditor({
    draftParams,
    onChange,
  }: DashboardWidgetEditorProps): ReactElement {
    if (fields.length === 0) {
      return (
        <p className="text-sm text-pb-text-muted">
          This section has no configurable settings.
        </p>
      );
    }
    return (
      <div className="space-y-4">
        {fields.map((field) => {
          const current = draftParams[field.key];
          const id = `pb-lama-param-${field.key}`;
          return (
            <div key={field.key}>
              <label htmlFor={id} className={LEGEND}>
                {LABELS[field.key] ?? field.key}
              </label>
              {/* eslint-disable-next-line ds/no-hand-built -- kept: a 34px number field on the raised fill; NumberInput is a 36px card-fill field, and no approved look row lists the change. */}
              <input
                id={id}
                type="number"
                min={field.min}
                max={field.max}
                step={1}
                value={typeof current === "number" ? current : ""}
                placeholder={`All (max ${field.max})`}
                onChange={(event) => {
                  const raw = event.target.value;
                  /* An EMPTY input DELETES the key, not sends 0 or NaN. Absent
                     means the shipped default — what every existing instance
                     holds — so clearing must return the widget to that, and
                     `strict()` would reject a present `undefined`. */
                  const next = { ...draftParams };
                  if (raw === "") {
                    delete next[field.key];
                  } else {
                    next[field.key] = Number(raw);
                  }
                  onChange(next);
                }}
                className="w-full bg-pb-card-elevated border border-pb-control-border rounded-md px-2.5 py-1.5 text-sm text-pb-text placeholder:text-pb-text-muted focus-frame"
              />
            </div>
          );
        })}
      </div>
    );
  }
  LamaParamsEditor.displayName = `LamaParamsEditor(${kind})`;
  return LamaParamsEditor;
}
